import { useState, useRef, useEffect } from "react";

interface PinInputProps {
  totalDigits?: number;
  onSubmit?: (otp: string) => void;
}

function PinInput(props: PinInputProps) {
  const [otp, setOtp] = useState<string[]>(
    new Array(props.totalDigits ?? 6).fill(""),
  );
  const [activeOtpIndex, setActiveOtpIndex] = useState<number>(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnKeyDown = (
    { key }: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (key === "Backspace") {
      const newOTP = [...otp];
      newOTP[index] = "";
      setOtp(newOTP);
      setActiveOtpIndex(Math.max(0, index - 1));
    } else if (key === "ArrowLeft") {
      setActiveOtpIndex(Math.max(0, index - 1));
    } else if (key === "ArrowRight") {
      setActiveOtpIndex(Math.min(otp.length - 1, index + 1));
    }
  };

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    const newOTP = [...otp];
    newOTP[activeOtpIndex] = value.substring(value.length - 1);
    setOtp(newOTP);

    if (value) {
      const nextIndex = Math.min(activeOtpIndex + 1, otp.length - 1);
      setActiveOtpIndex(nextIndex);

      if (newOTP.every(val => val) && props.onSubmit) {
        props.onSubmit(newOTP.join(""));
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, props.totalDigits ?? 6);
    const newOTP = [...otp];
    
    for (let i = 0; i < pastedData.length; i++) {
      if (i < newOTP.length) {
        newOTP[i] = pastedData[i];
      }
    }
    
    setOtp(newOTP);
    setActiveOtpIndex(Math.min(pastedData.length, newOTP.length - 1));

    if (newOTP.every(val => val) && props.onSubmit) {
      props.onSubmit(newOTP.join(""));
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeOtpIndex]);

  return (
    <div className="flex items-center justify-center gap-2">
      {otp.map((digit, index) => {
        return (
          <div key={index} className="h-14 w-14 relative">
            <input
              ref={index === activeOtpIndex ? inputRef : null}
              className={`input spin-button-none h-14 w-14 text-center font-bold uppercase transition-all
                ${index === activeOtpIndex ? "border-primary ring-2 ring-primary/20" : ""}
                ${digit ? "bg-muted" : ""}`}
              maxLength={1}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={handleChange}
              onKeyDown={(e) => handleOnKeyDown(e, index)}
              onPaste={handlePaste}
              value={digit}
            />
          </div>
        );
      })}
    </div>
  );
}

export default PinInput;
